import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import theme from '../../styles/theme';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { formatSpeed, formatDistance, formatFuel } from '../../utils/unitConversions';

/**
 * Season Analytics Dashboard
 * 
 * Comprehensive season statistics with:
 * - Total distance, fuel, hours
 * - Monthly trends
 * - Vessel performance comparison
 * - Fuel efficiency analysis
 */

const SeasonDashboard = ({ year }) => {
  const { preferences } = useUserPreferences();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview'); // overview, monthly, vessels

  useEffect(() => {
    if (year) {
      fetchSeasonAnalytics();
    }
  }, [year]);

  const fetchSeasonAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/analytics/season/${year}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching season analytics:', error);
      toast.error('Failed to load season analytics');
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.125rem', color: theme.colors.text.secondary }}>
          Loading season analytics...
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.125rem', color: theme.colors.text.secondary }}>
          No season data available
        </div>
      </div>
    );
  }

  const stats = analytics.stats;

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Season Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '0.5rem' }}>
          {year} Boating Season
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '1.125rem' }}>
          Comprehensive analytics and performance metrics
        </p>
      </div>

      {/* Key Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Total Distance */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Total Distance
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.primary.main, marginBottom: '0.25rem' }}>
            {stats.totalDistance.nm.toFixed(1)}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            nautical miles ({stats.totalDistance.km.toFixed(1)} km)
          </div>
        </div>

        {/* Total Trips */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Total Trips
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.success.main, marginBottom: '0.25rem' }}>
            {stats.totalTrips}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            {stats.averageTripDistance} nm avg
          </div>
        </div>

        {/* Total Fuel */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Total Fuel Used
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.error.main, marginBottom: '0.25rem' }}>
            {stats.totalFuel.toFixed(1)}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            liters ({(stats.totalFuel * 0.264172).toFixed(1)} gal)
          </div>
        </div>

        {/* Fuel Efficiency */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Fuel Efficiency
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.warning.main, marginBottom: '0.25rem' }}>
            {stats.fuelEfficiency}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            nm per liter
          </div>
        </div>

        {/* Total Hours */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Total Hours
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.secondary.main, marginBottom: '0.25rem' }}>
            {stats.totalDuration.hours}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            engine hours
          </div>
        </div>

        {/* Max Speed */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral[200]}`
        }}>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginBottom: '0.5rem', fontWeight: '500' }}>
            Max Speed
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: theme.colors.primary.dark, marginBottom: '0.25rem' }}>
            {stats.maxSpeed.toFixed(1)}
          </div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            knots
          </div>
        </div>
      </div>

      {/* View Selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: `2px solid ${theme.colors.neutral[200]}` }}>
          {['overview', 'monthly', 'vessels'].map(view => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: `3px solid ${selectedView === view ? theme.colors.primary.main : 'transparent'}`,
                color: selectedView === view ? theme.colors.primary.main : theme.colors.text.secondary,
                fontWeight: selectedView === view ? '600' : '500',
                cursor: 'pointer',
                transition: theme.transitions.default,
                textTransform: 'capitalize'
              }}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      {/* Overview - Trip List */}
      {selectedView === 'overview' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: `1px solid ${theme.colors.neutral[200]}`,
            backgroundColor: theme.colors.neutral[50]
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
              All Trips ({analytics.trips.length})
            </h3>
          </div>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Trip</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Vessel</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Distance</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Fuel</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `2px solid ${theme.colors.neutral[200]}`, fontWeight: '600' }}>Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {analytics.trips.map((trip, index) => (
                  <tr 
                    key={trip.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : theme.colors.neutral[50],
                      cursor: 'pointer'
                    }}
                    onClick={() => window.location.href = `/trips/${trip.id}`}
                  >
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}` }}>
                      {trip.name}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}` }}>
                      {trip.vessel}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}`, fontSize: '0.875rem' }}>
                      {new Date(trip.startTime).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}` }}>
                      {preferences ? formatDistance(trip.distance, preferences.distance_unit, 2) : `${trip.distance.toFixed(2)} nm`}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}` }}>
                      {preferences ? formatFuel(trip.fuel, preferences.fuel_units, 2) : `${trip.fuel.toFixed(2)} L`}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.colors.neutral[200]}` }}>
                      {trip.fuel > 0 ? (trip.distance / trip.fuel).toFixed(2) : 'N/A'} {preferences ? (preferences.distance_unit === 'kilometers' ? 'km/L' : preferences.distance_unit === 'statute_miles' ? 'mi/gal' : 'nm/L') : 'nm/L'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly View */}
      {selectedView === 'monthly' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
            Monthly Breakdown
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
            {Object.entries(analytics.monthlyData).map(([month, data]) => {
              if (data.trips === 0) return null;
              return (
                <div 
                  key={month}
                  style={{
                    padding: '1rem',
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.neutral[200]}`,
                    backgroundColor: theme.colors.neutral[50]
                  }}
                >
                  <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.5rem', color: theme.colors.primary.main }}>
                    {monthNames[parseInt(month) - 1]}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div>Trips: <strong>{data.trips}</strong></div>
                    <div>Distance: <strong>{preferences ? formatDistance(data.distance, preferences.distance_unit, 1) : `${data.distance.toFixed(1)} nm`}</strong></div>
                    <div>Fuel: <strong>{preferences ? formatFuel(data.fuel, preferences.fuel_units, 1) : `${data.fuel.toFixed(1)} L`}</strong></div>
                    <div>Hours: <strong>{data.hours.toFixed(1)} hrs</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vessel Comparison */}
      {selectedView === 'vessels' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.md,
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
            Vessel Performance Comparison
          </h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {Object.entries(analytics.vesselStats).map(([vessel, stats]) => (
              <div 
                key={vessel}
                style={{
                  padding: '1.5rem',
                  borderRadius: theme.borderRadius.md,
                  border: `2px solid ${theme.colors.primary.light}`,
                  backgroundColor: 'white'
                }}
              >
                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: theme.colors.primary.main }}>
                  {vessel}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginBottom: '0.25rem' }}>Trips</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{stats.trips}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginBottom: '0.25rem' }}>Distance</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{preferences ? formatDistance(stats.distance, preferences.distance_unit, 1) : `${stats.distance.toFixed(1)} nm`}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginBottom: '0.25rem' }}>Fuel Used</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{preferences ? formatFuel(stats.fuel, preferences.fuel_units, 1) : `${stats.fuel.toFixed(1)} L`}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginBottom: '0.25rem' }}>Efficiency</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                      {stats.fuel > 0 ? (stats.distance / stats.fuel).toFixed(2) : 'N/A'} {preferences ? (preferences.distance_unit === 'kilometers' ? 'km/L' : preferences.distance_unit === 'statute_miles' ? 'mi/gal' : 'nm/L') : 'nm/L'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginBottom: '0.25rem' }}>Engine Hours</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{stats.hours.toFixed(1)} hrs</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonDashboard;

